// @flow

import { startOfDay, format } from 'date-fns';
import * as R from 'ramda';
import { ARTICLES_LIST, ACTIVE_ANNOUNCEMENTS_LIST } from '~/gql';

import { HttpService } from '~/services';
import { renameKeys, parseBool } from '~/helpers';

const formatDate = value => format(value, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");

const sortWithPinnedAndBookmarked = R.sortWith([
  R.descend(R.prop('pinned')),
  R.descend(R.prop('bookmarked')),
]);

const setListBookmark = (id, isBookmarked) =>
  R.map(
    R.over(
      R.lensProp('items'),
      R.map(
        R.ifElse(
          R.propEq('id', id),
          R.assoc('bookmarked', isBookmarked),
          R.identity,
        ),
      ),
    ),
  );

const withSameDay = R.pipe(
  R.prop('PublishedDateTimeUtc'),
  startOfDay,
  formatDate,
);

const getArticlesList = async ({
  params = {},
  endpoint,
  fieldName,
  itemType,
  groupType,
}) => {
  const { data } = await HttpService.client.get(endpoint, {
    params: { SortBy: 'PublishedDateTimeUtc', PageSize: 1000, ...params },
  });

  const articlesList: ArticlesListItem[] = R.pipe(
    R.propOr([], fieldName),
    R.groupBy(withSameDay),
    R.mapObjIndexed((values, date) => ({
      date,
      items: R.map(
        R.pipe(
          renameKeys({
            Id: 'id',
            PublishedDateTimeUtc: 'date',
            Title: 'title',
            Pin: 'pinned',
            Bookmark: 'bookmarked',
            Type: 'type',
          }),
          R.evolve({
            pinned: parseBool,
            bookmarked: parseBool,
          }),
          R.assoc('__typename', groupType),
          o => {
            if (typeof o.bookmarked === 'undefined') o.bookmarked = false;
            if (typeof o.pinned === 'undefined') o.pinned = false;
            return o;
          },
        ),
        values,
      ),
      __typename: itemType,
    })),
    R.values,
  )(data);

  return articlesList;
};

const getArticle = async ({ endpoint, fieldName, groupType, articleId }) => {
  const { data } = await HttpService.client.get(endpoint, {
    params: { id: articleId },
  });

  // $FlowIssue
  const article: Article = R.pipe(
    R.propOr({}, fieldName),
    // Apply default subcategory
    R.merge({ Subcategory: null }),
    renameKeys({
      Id: 'id',
      DateTimeUtc: 'date',
      Title: 'title',
      Content: 'content',
      URL: 'content',
      Category: 'category',
      Subcategory: 'subcategory',
      Bookmark: 'bookmarked',
      Pin: 'pinned',
    }),
    R.evolve({
      bookmarked: parseBool,
      pinned: parseBool,
    }),
    R.assoc('id', articleId),
    R.assoc('__typename', groupType),
  )(data);

  return article;
};

export const resolvers = {
  Query: {
    articlesList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { type, group }: ArticlesListQueryInput,
      { cache }: ApolloCache,
    ): Promise<?(ArticlesListItem[])> => {
      let articlesList;

      switch (type) {
        case 'announcements':
          articlesList = await getArticlesList({
            endpoint: '/Information/GetAnnouncementList',
            fieldName: 'AnnouncementLine',
            itemType: 'AnnouncementsListItem',
            groupType: 'AnnouncementsListGroupItem',
          });
          break;
        case 'news':
          articlesList = await getArticlesList({
            endpoint: '/Information/GetNewsList',
            fieldName: 'NewsLine',
            itemType: 'NesListItem',
            groupType: 'NewsListGroupItem',
          });
          break;
        case 'promotions':
          articlesList = await getArticlesList({
            endpoint: '/Information/GetPromotionList',
            fieldName: 'PromotionLine',
            itemType: 'PromotionsListItem',
            groupType: 'PromotionsListGroupItem',
          });
          break;
        case 'tutorials':
          articlesList = await getArticlesList({
            endpoint: '/Information/GetTutorialVideoList',
            fieldName: 'VideoList',
            itemType: 'TutorialVideosListItem',
            groupType: 'TutorialVideosListGroupItem',
            params: { Group: group ? group[0].toUpperCase() : 'B' },
          });
          break;
        case 'system':
          switch (group) {
            case 'video':
              articlesList = await getArticlesList({
                endpoint: '/Information/GetVideoGuideList',
                fieldName: 'VideoGuideLine',
                itemType: 'VideoGuideListItem',
                groupType: 'VideoGuideListGroupItem',
              });
              break;
            default:
              articlesList = await getArticlesList({
                endpoint: '/Information/GetUserGuideList',
                fieldName: 'UserGuideLine',
                itemType: 'UserGuideListItem',
                groupType: 'UserGuideListGroupItem',
              });
              break;
          }
          break;
        default:
          break;
      }

      cache.writeQuery({
        query: ARTICLES_LIST,
        data: { articlesList },
        variables: { type, group },
      });

      return articlesList;
    },
    article: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { articleId, type, group }: ArticleQueryInput,
      { cache }: ApolloCache,
    ): Promise<?Article> => {
      let article;

      switch (type) {
        case 'announcements':
          article = await getArticle({
            endpoint: '/Information/GetAnnouncementContent',
            fieldName: 'AnnouncementDetails',
            groupType: 'AnnouncementsListGroupItem',
            articleId,
          });
          break;
        case 'news':
          article = await getArticle({
            endpoint: '/Information/GetNewsContent',
            fieldName: 'NewsDetails',
            groupType: 'NewsListGroupItem',
            articleId,
          });
          break;
        case 'promotions':
          article = await getArticle({
            endpoint: '/Information/GetPromotionContent',
            fieldName: 'PromotionDetails',
            groupType: 'PromotionsListGroupItem',
            articleId,
          });
          break;
        case 'tutorials':
          article = await getArticle({
            endpoint: '/Information/GetTutorialVideoContent',
            fieldName: 'TutorialDetails',
            groupType: 'TutorialVideosListGroupItem',
            articleId,
          });
          break;
        case 'system':
          switch (group) {
            case 'video':
              article = await getArticle({
                endpoint: '/Information/GetVideoGuideContent',
                fieldName: 'VideoGuideDetails',
                groupType: 'VideoGuideListGroupItem',
                articleId,
              });
              break;
            default:
              article = await getArticle({
                endpoint: '/Information/GetUserGuideContent',
                fieldName: 'UserGuideDetails',
                groupType: 'UserGuideListGroupItem',
                articleId,
              });
              break;
          }
          break;
        default:
          break;
      }

      cache.writeData({
        data: { article },
        variables: { type, group, articleId },
      });

      return article;
    },
    tutorialVideosGroups: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<TutorialVideosGroupsListItem[]> => {
      const {
        data: { Tutorial },
      } = await HttpService.client.get('/Information/GetTutorialVideoGroup');

      const tutorialVideosGroupsList: TutorialVideosGroupsListItem[] = R.map(
        R.pipe(
          renameKeys({
            Group: 'group',
            Title: 'title',
            Visible: 'isVisible',
          }),
          R.assoc('__typename', 'TutorialVideosGroupsListItem'),
        ),
        Tutorial,
      );

      cache.writeData({
        data: { tutorialVideosGroupsList },
      });

      return tutorialVideosGroupsList;
    },
  },

  Mutation: {
    addBookmark: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { articleId, type }: { articleId: string, type: BookmarkType },
      // $FlowIssue
      { cache }: AppoloCache, // eslint-disable-line no-unused-vars
    ): Promise<MessagePayload> => {
      const endpoints = {
        announcements: '/Information/AddBookmarkAnnouncement',
        news: '/Information/AddBookmarkNews',
      };
      const params = { id: [`${articleId}`] };

      if (type === 'announcements' || type === 'news') {
        const { activeAnnouncementsList } = R.tryCatch(
          () => cache.readQuery({ query: ACTIVE_ANNOUNCEMENTS_LIST }),
          () => ({ activeAnnouncementsList: [] }),
        )();

        cache.writeQuery({
          query: ACTIVE_ANNOUNCEMENTS_LIST,
          data: {
            activeAnnouncementsList: sortWithPinnedAndBookmarked(
              R.map(
                R.ifElse(
                  R.propEq('id', articleId),
                  R.assoc('bookmarked', true),
                  R.identity,
                ),
              )(activeAnnouncementsList),
            ),
          },
        });
      }

      const { articlesList } = R.tryCatch(
        () =>
          cache.readQuery({
            query: ARTICLES_LIST,
            variables: { type },
          }),
        () => ({ articlesList: [] }),
      )();

      cache.writeQuery({
        query: ARTICLES_LIST,
        data: {
          articlesList: setListBookmark(articleId, true)(articlesList),
        },
        variables: { type },
      });

      try {
        await HttpService.client.post(endpoints[type], params);
      } catch (e) {}

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    deleteBookmark: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { articleId, type }: { articleId: string, type: BookmarkType },
      // $FlowIssue
      { cache }, // eslint-disable-line no-unused-vars
    ): Promise<MessagePayload> => {
      const endpoints = {
        announcements: '/Information/DeleteBookmarkAnnouncement',
        news: '/Information/DeleteBookmarkNews',
      };
      const params = { id: [`${articleId}`] };

      if (type === 'announcements' || type === 'news') {
        const { activeAnnouncementsList } = R.tryCatch(
          () => cache.readQuery({ query: ACTIVE_ANNOUNCEMENTS_LIST }),
          () => ({ activeAnnouncementsList: [] }),
        )();

        cache.writeQuery({
          query: ACTIVE_ANNOUNCEMENTS_LIST,
          data: {
            activeAnnouncementsList: sortWithPinnedAndBookmarked(
              R.map(
                R.ifElse(
                  R.propEq('id', articleId),
                  R.assoc('bookmarked', false),
                  R.identity,
                ),
              )(activeAnnouncementsList),
            ),
          },
        });
      }

      const { articlesList } = R.tryCatch(
        () =>
          cache.readQuery({
            query: ARTICLES_LIST,
            variables: { type },
          }),
        () => ({ articlesList: [] }),
      )();

      cache.writeQuery({
        query: ARTICLES_LIST,
        data: {
          articlesList: setListBookmark(articleId, false)(articlesList),
        },
        variables: { type },
      });

      try {
        await HttpService.client.post(endpoints[type], params);
      } catch (e) {}

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
  },
};
